import React, { useEffect, useState, useCallback } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { toast } from 'sonner';
import { Megaphone, Globe, Sparkles, Send, Mail, RefreshCw, Plus, Trash2, Rocket } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const BRANCHES = [
  { key: 'fit', label: 'Fit' }, { key: 'care', label: 'Care' }, { key: 'dance', label: 'Dance' },
  { key: 'beauty', label: 'Beauty' }, { key: 'dental', label: 'Dental' }, { key: 'mobility', label: 'Mobility' },
];
const STATUS_LABEL = {
  draft: 'Entwurf', pending_confirmation: 'E-Mail offen', confirmed: 'Bestätigt',
  generated: 'Erstellt', needs_review: 'Zur Freigabe', sent: 'Gesendet', opened: 'Geöffnet',
  demo_booked: 'Vorführung', registered: 'Registriert', declined: 'Abgelehnt', unsubscribed: 'Abgemeldet',
};
const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-600', pending_confirmation: 'bg-slate-100 text-slate-500',
  confirmed: 'bg-sky-100 text-sky-700', generated: 'bg-blue-100 text-blue-700',
  needs_review: 'bg-orange-100 text-orange-700', sent: 'bg-amber-100 text-amber-700',
  opened: 'bg-violet-100 text-violet-700', registered: 'bg-emerald-100 text-emerald-700',
  unsubscribed: 'bg-rose-100 text-rose-700', declined: 'bg-rose-100 text-rose-700', demo_booked: 'bg-indigo-100 text-indigo-700',
};

const emptyLead = { company_name: '', branch: 'fit', website: '', email: '', salutation: '', contact_name: '', language: 'de', internal_note: '' };

export default function SalesGeneratorPage() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('');
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newLead, setNewLead] = useState(emptyLead);
  const [busy, setBusy] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    try { const { data } = await superAdminApi.salesGetSettings(); setSettings({ ...data, recaptcha_secret_key: '', _notify_raw: (data.notify_emails || []).join(', ') }); } catch {}
  }, []);
  const toggleSettings = () => setShowSettings(s => { const n = !s; if (n) loadSettings(); return n; });
  const saveSettings = async () => {
    try {
      await superAdminApi.salesSaveSettings({
        recaptcha_enabled: !!settings.recaptcha_enabled,
        recaptcha_site_key: settings.recaptcha_site_key || '',
        recaptcha_secret_key: settings.recaptcha_secret_key || '',
        notify_emails: (settings._notify_raw || '').split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Einstellungen gespeichert'); setShowSettings(false); loadSettings();
    } catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await superAdminApi.salesLeads(filter ? { status: filter } : {});
      setLeads(data || []);
    } catch { toast.error('Laden fehlgeschlagen'); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openLead = async (id) => {
    const { data } = await superAdminApi.salesGetLead(id);
    setSel(data); setDraft({ ...data, colors: data.colors || {} }); setCreating(false);
  };

  const createLead = async () => {
    if (!newLead.company_name.trim()) { toast.error('Firmenname fehlt'); return; }
    try {
      const { data } = await superAdminApi.salesCreateLead(newLead);
      toast.success('Interessent angelegt');
      setNewLead(emptyLead); setCreating(false); await load(); openLead(data.id);
    } catch { toast.error('Anlegen fehlgeschlagen'); }
  };

  const buildPayload = (extra = {}) => ({
    company_name: draft.company_name, branch: draft.branch, website: draft.website || '',
    email: draft.email || '', salutation: draft.salutation || '', contact_name: draft.contact_name || '',
    language: draft.language || 'de', logo_url: draft.logo_url || '', description: draft.description || '',
    services: draft.services || [], prices: draft.prices || '', colors: draft.colors || {},
    booking_system: draft.booking_system || '', internal_note: draft.internal_note || '',
    email_subject: draft.email_subject || '', email_intro: draft.email_intro || '',
    ai_instructions: draft.ai_instructions || '', ...extra,
  });

  const persist = async (extra = {}) => {
    const { data } = await superAdminApi.salesUpdateLead(sel.id, buildPayload(extra));
    setSel(data); setDraft({ ...data, colors: data.colors || {} });
    return data;
  };

  const saveDraft = async () => {
    try { await persist(); toast.success('Gespeichert'); load(); }
    catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const act = async (name, fn, okMsg) => {
    setBusy(name);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); await load(); if (sel) await openLead(sel.id); return r; }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const readWebsite = () => act('web', async () => { await persist(); return superAdminApi.salesReadWebsite(sel.id); }, 'Website gelesen – Felder vorausgefüllt');
  const generate = () => act('gen', async () => { await persist(); return superAdminApi.salesGenerate(sel.id, window.location.origin); }, 'Angebot erstellt');
  const refine = () => act('refine', async () => { await persist(); return superAdminApi.salesRefine(sel.id, draft.ai_instructions || '', window.location.origin); }, 'KI-Version erstellt – bitte prüfen & speichern');
  const testSend = () => act('test', async () => {
    const r = await superAdminApi.salesTestSend(sel.id, draft.email || prompt('Test-E-Mail an:') || '');
    if (r?.data?.ok) toast.success('Testversand ok'); else toast.warning('Versand nicht möglich (SMTP/Preview)');
  });
  const sendReal = () => { if (window.confirm('Angebot jetzt an den Interessenten senden?')) act('send', () => superAdminApi.salesSend(sel.id), 'Gesendet'); };
  const convert = () => { if (window.confirm('Interessent in einen echten Mandanten umwandeln?')) act('conv', async () => { const r = await superAdminApi.salesConvert(sel.id, ''); toast.success(`Mandant erstellt: ${r.data.tenant.slug}`); }); };
  const delLead = async (id) => { if (!window.confirm('Interessent löschen?')) return; await superAdminApi.salesDeleteLead(id); if (sel?.id === id) { setSel(null); setDraft(null); } load(); };

  const setColor = (k, v) => setDraft(d => ({ ...d, colors: { ...(d.colors || {}), [k]: v } }));

  return (
    <div data-testid="sales-generator-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white"><Megaphone className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold">Verkauf</h1>
          <p className="text-sm text-slate-500">Persönliche Angebote erstellen – Website lesen, Vorschau, PDF & E-Mail</p>
        </div>
        <button onClick={toggleSettings} data-testid="sales-settings-btn"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm hover:bg-slate-50">Einstellungen</button>
        <button onClick={() => { setCreating(true); setSel(null); }} data-testid="sales-new-offer-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
          <Plus className="h-4 w-4" /> Neues Angebot
        </button>
      </div>

      {showSettings && settings && (
        <div data-testid="sales-settings-panel" className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 max-w-2xl">
          <h2 className="text-lg font-semibold mb-1">Verkaufs-Einstellungen</h2>
          <p className="text-xs text-emerald-700 mb-4">✔ Vorschauen werden <b>nie automatisch</b> gesendet – immer manuelle Prüfung & Freigabe.</p>
          <div className="space-y-3">
            <Field label="Benachrichtigungs-E-Mails (Komma-getrennt) – wer wird bei neuer Vorschau informiert">
              <input data-testid="sales-notify-emails" className="inp" value={settings._notify_raw || ''}
                onChange={e => setSettings({ ...settings, _notify_raw: e.target.value })} placeholder="verkauf@invictaflow.ch" />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="sales-recaptcha-enabled" checked={!!settings.recaptcha_enabled}
                onChange={e => setSettings({ ...settings, recaptcha_enabled: e.target.checked })} />
              reCAPTCHA auf den Landingpages aktiv
            </label>
            {settings.recaptcha_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="reCAPTCHA Site-Key"><input className="inp" value={settings.recaptcha_site_key || ''} onChange={e => setSettings({ ...settings, recaptcha_site_key: e.target.value })} /></Field>
                <Field label="reCAPTCHA Secret-Key"><input className="inp" type="password" value={settings.recaptcha_secret_key || ''} placeholder={settings.recaptcha_secret_set ? '•••••• gespeichert (leer lassen = unverändert)' : ''} onChange={e => setSettings({ ...settings, recaptcha_secret_key: e.target.value })} /></Field>
              </div>
            )}
            <p className="text-xs text-slate-500">Landingpage-Adresse pro Branche: <code>/vorschau/&lt;branche&gt;</code> (z. B. /vorschau/fit). Echte Subdomains (fit.invictaflow.ch) werden beim Deployment verbunden.</p>
            <button onClick={saveSettings} data-testid="sales-settings-save" className="rounded-xl bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700">Speichern</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* Liste */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="sales-status-filter"
            className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle Status</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="space-y-1 max-h-[70vh] overflow-auto">
            {leads.length === 0 && <p className="p-4 text-sm text-slate-400">Noch keine Interessenten.</p>}
            {leads.map(l => (
              <div key={l.id} onClick={() => openLead(l.id)} data-testid={`sales-lead-row-${l.id}`}
                className={`cursor-pointer rounded-xl px-3 py-2.5 ${sel?.id === l.id ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'hover:bg-slate-50'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{l.company_name}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[l.status] || 'bg-slate-100'}`}>{STATUS_LABEL[l.status] || l.status}</span>
                </div>
                <div className="text-xs text-slate-500">{(BRANCHES.find(b => b.key === l.branch) || {}).label} · {l.email || 'keine E-Mail'}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail / Neu */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          {creating && (
            <div data-testid="sales-new-form" className="space-y-3 max-w-xl">
              <h2 className="text-lg font-semibold">Neues Angebot</h2>
              <Field label="Firmenname *"><input data-testid="sales-new-company" className="inp" value={newLead.company_name} onChange={e => setNewLead({ ...newLead, company_name: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Branche"><select data-testid="sales-new-branch" className="inp" value={newLead.branch} onChange={e => setNewLead({ ...newLead, branch: e.target.value })}>{BRANCHES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></Field>
                <Field label="Sprache"><select className="inp" value={newLead.language} onChange={e => setNewLead({ ...newLead, language: e.target.value })}><option>de</option><option>fr</option><option>it</option><option>en</option></select></Field>
              </div>
              <Field label="Website"><input data-testid="sales-new-website" className="inp" placeholder="https://…" value={newLead.website} onChange={e => setNewLead({ ...newLead, website: e.target.value })} /></Field>
              <Field label="E-Mail"><input className="inp" value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} /></Field>
              <Field label="Ansprechperson"><input className="inp" value={newLead.contact_name} onChange={e => setNewLead({ ...newLead, contact_name: e.target.value })} /></Field>
              <div className="flex gap-2 pt-1">
                <button onClick={createLead} data-testid="sales-create-submit" className="rounded-xl bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700">Anlegen</button>
                <button onClick={() => setCreating(false)} className="rounded-xl border px-4 py-2 text-sm">Abbrechen</button>
              </div>
            </div>
          )}

          {!creating && !sel && <p className="text-slate-400 text-sm p-8 text-center">Wähle links einen Interessenten oder lege ein neues Angebot an.</p>}

          {!creating && sel && draft && (
            <div data-testid="sales-detail" className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">{draft.company_name}</h2>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[sel.status]}`}>{STATUS_LABEL[sel.status]}</span>
                  <button onClick={() => delLead(sel.id)} data-testid="sales-delete-btn" className="ml-auto text-slate-400 hover:text-rose-600" title="Löschen"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Firmenname"><input className="inp" value={draft.company_name} onChange={e => setDraft({ ...draft, company_name: e.target.value })} /></Field>
                  <Field label="Branche"><select className="inp" value={draft.branch} onChange={e => setDraft({ ...draft, branch: e.target.value })}>{BRANCHES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></Field>
                </div>
                <Field label="Website">
                  <div className="flex gap-2">
                    <input data-testid="sales-website-input" className="inp" value={draft.website || ''} onChange={e => setDraft({ ...draft, website: e.target.value })} />
                    <button onClick={readWebsite} disabled={busy === 'web'} data-testid="sales-read-website-btn" className="shrink-0 inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 text-sm hover:bg-slate-50"><Globe className="h-4 w-4" />{busy === 'web' ? '…' : 'Lesen'}</button>
                  </div>
                </Field>
                <Field label="E-Mail"><input data-testid="sales-email-input" className="inp" value={draft.email || ''} onChange={e => setDraft({ ...draft, email: e.target.value })} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Primärfarbe"><input type="color" className="h-9 w-full rounded-lg border" value={draft.colors?.primary || '#2F6F6D'} onChange={e => setColor('primary', e.target.value)} /></Field>
                  <Field label="Akzentfarbe"><input type="color" className="h-9 w-full rounded-lg border" value={draft.colors?.accent || '#E8A84C'} onChange={e => setColor('accent', e.target.value)} /></Field>
                </div>
                <Field label="Logo-URL"><input className="inp" value={draft.logo_url || ''} onChange={e => setDraft({ ...draft, logo_url: e.target.value })} /></Field>
                <Field label="Beschreibung"><textarea rows={3} className="inp" value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })} /></Field>
                <Field label="E-Mail-Einleitung (wird im E-Mail-Text verwendet)"><textarea rows={2} data-testid="sales-email-intro" className="inp" value={draft.email_intro || ''} onChange={e => setDraft({ ...draft, email_intro: e.target.value })} /></Field>
                <Field label="Leistungen (Komma-getrennt)"><input className="inp" value={(draft.services || []).join(', ')} onChange={e => setDraft({ ...draft, services: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} /></Field>
                {draft.booking_system && <p className="text-xs text-amber-700">Erkanntes System: {draft.booking_system} → Wechselangebot möglich</p>}
                {sel.source === 'meta' && <p className="text-xs text-slate-500">Quelle: Meta-Werbung{sel.utm?.utm_campaign ? ` · Kampagne ${sel.utm.utm_campaign}` : ''}</p>}
                <div className="rounded-xl bg-violet-50 border border-violet-100 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-violet-800"><Sparkles className="h-4 w-4" />Mit kurzen Worten anpassen (PDF & E-Mail)</div>
                  <textarea rows={2} data-testid="sales-ai-instructions" className="inp" placeholder="z.B. kurz und sachlich, Fokus auf Online-Buchung und WhatsApp"
                    value={draft.ai_instructions || ''} onChange={e => setDraft({ ...draft, ai_instructions: e.target.value })} />
                  <button onClick={refine} disabled={busy === 'refine'} data-testid="sales-ai-refine-btn"
                    className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-700">
                    <Sparkles className="h-4 w-4" />{busy === 'refine' ? 'Erstelle…' : 'Mit KI anpassen'}
                  </button>
                  <p className="text-[11px] text-violet-500">Die KI formuliert Beschreibung, E-Mail-Einleitung und Betreff neu. Danach „Speichern" drücken, um die Version zu sichern.</p>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button onClick={saveDraft} data-testid="sales-save-btn" className="rounded-xl border px-4 py-2 text-sm hover:bg-slate-50">Speichern</button>
                  <button onClick={generate} disabled={busy === 'gen'} data-testid="sales-generate-btn" className="inline-flex items-center gap-1 rounded-xl bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"><Sparkles className="h-4 w-4" />{busy === 'gen' ? 'Erstelle…' : 'Generieren'}</button>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-600">Vorschau</h3>
                {sel.pdf_ready ? (
                  <>
                    <iframe title="pdf" data-testid="sales-pdf-preview" src={`${BASE}/api/sales/public/${sel.token}/pdf?preview=1`} className="w-full h-[420px] rounded-xl border border-slate-200" />
                    {(sel.email_subject_options?.length > 0 || draft.email_subject) && (
                      <Field label="Betreff (frei bearbeitbar – Vorschläge verfügbar)">
                        <input data-testid="sales-subject-input" list="sales-subject-opts" className="inp"
                          value={draft.email_subject || ''}
                          onChange={e => setDraft(d => ({ ...d, email_subject: e.target.value }))}
                          onBlur={async () => { try { await persist({ email_subject: draft.email_subject || '' }); } catch {} }} />
                        <datalist id="sales-subject-opts">
                          {(sel.email_subject_options || []).map((s, i) => <option key={i} value={s} />)}
                        </datalist>
                      </Field>
                    )}
                    {sel.quality_missing?.length > 0 && (
                      <div data-testid="sales-quality-missing" className="rounded-xl bg-orange-50 border border-orange-100 p-3 text-xs text-orange-700">
                        Bitte vor dem Senden ergänzen: {sel.quality_missing.join(' · ')}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={testSend} disabled={busy === 'test'} data-testid="sales-testsend-btn" className="inline-flex items-center gap-1 rounded-xl border px-4 py-2 text-sm hover:bg-slate-50"><Mail className="h-4 w-4" />Testversand</button>
                      <button onClick={sendReal} disabled={busy === 'send'} data-testid="sales-send-btn" className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700"><Send className="h-4 w-4" />Freigeben & senden</button>
                      <button onClick={convert} disabled={busy === 'conv'} data-testid="sales-convert-btn" className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-700"><Rocket className="h-4 w-4" />In Mandant umwandeln</button>
                    </div>
                    <p className="text-xs text-slate-400">Jede Vorschau wird <b>manuell</b> geprüft und erst mit „Freigeben &amp; senden" verschickt – nie automatisch. Echter E-Mail-Versand benötigt invictaflow.ch-SMTP im Deployment.</p>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
                    <RefreshCw className="h-5 w-5 mx-auto mb-2" />Noch kein Angebot erstellt. Fülle die Felder und klicke „Generieren".
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:0.6rem;padding:0.5rem 0.75rem;font-size:0.875rem}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>{children}</label>;
}
